// @flow
import React from 'react';
import { connect } from 'react-redux';
import { get, isEqual } from 'lodash';
import { isEnterKey, isPresent, keepKeysRef, openInBrowser, UserNotifications } from '@helpers/helpers';
import { dashboardPageUrl } from '@context/user';
import { Icon, Button, Modal, Spinner, ClickableList, FormattedText, MobilePhoneLinks } from '@elements';
import type { Doctors, Patient, Clinic } from '@types';

type Props = {
  formChange: Function,
  setModal: Function,
  selectedPatient: ?Patient,
  doctors: Doctors,
  isNew: boolean,
  patientLoading: boolean,
  clinic: Clinic,
  appointmentId: ?(string | number),
  onOpenProfile?: ?Function,
  accessToken: string,
  _: Function,
};

export type MissingPatientField = 'birthdate' | 'cpf' | 'mobilePhone' | 'email' | 'address';

const MISSING_FIELD_LABELS: { [MissingPatientField]: string } = {
  birthdate: 'label/patient_form/birthdate',
  cpf: 'label/patient_form/cpf',
  mobilePhone: 'label/patient_form/mobile_phone',
  email: 'label/patient_form/email',
  address: 'label/patient_info/address',
};

// What the registration still lacks. Only the full patient (GET /patients/:id) knows: the appointment
// brings the name and phones alone, so nothing shows while it loads, or when the profile is hidden from
// this user. Any part of the address counts as an address. CPF is Brazil's: an international clinic
// doesn't ask for it.
export const missingPatientFields = (patient: ?Object, international: ?boolean = false): Array<MissingPatientField> => {
  if (!patient || patient.full !== true) return [];

  const missing = [];
  if (!isPresent(patient.birthdate)) missing.push('birthdate');
  if (!international && !isPresent(patient.cpf)) missing.push('cpf');
  if (!isPresent(patient.mobilePhone)) missing.push('mobilePhone');
  if (!isPresent(patient.email)) missing.push('email');
  const address = [
    patient.addressStreet, patient.addressNumber, patient.addressNeighborhood,
    patient.addressZipcode, patient.addressCity, patient.addressState,
  ];
  if (!address.some(isPresent)) missing.push('address');
  return missing;
};

// The web dashboard's page for this appointment's patient (api app/appointments#patient_link), signed in
// with the user's token. There the user generates the patient's own link and copies it or sends it by
// WhatsApp. This URL carries the user's token: it is for the user's browser, never for the patient.
export const patientLinkPageUrl = (accessToken: string, clinicId: any, appointmentId: any): string => (
  dashboardPageUrl(accessToken, `/app/clinics/${clinicId}/appointments/${appointmentId}/patient_link`)
);

const resetSelectionItems = (_: Function) => [
  {
    className: 'listItem resetSelectionItem',
    customRendererData: {
      icon: 'close-circle',
      text: _('label/patient_info/select_another'),
    },
  },
];

const resetSelectionRenderer = (item: Object) => (
  <span key="appointmentModalText">
    <Icon iconName={item.icon} className="icon" size={24} />
    <span>{item.text}</span>
  </span>
);

type RowProps = { label: string, children: any, actions?: any };

const InfoRow = ({ label, children, actions }: RowProps) => (
  <li className="patientSummaryRow">
    <div className="patientSummaryRowText">
      <span className="label">{label}</span>
      <span className="value">{children}</span>
    </div>
    {actions}
  </li>
);

class PatientInfoModal extends React.Component<Props> {
  shouldComponentUpdate(nextProps: Props) {
    return !isEqual(this.props, nextProps);
  }

  render() {
    const {
      selectedPatient, doctors, isNew, patientLoading, _, clinic, appointmentId, onOpenProfile,
    } = this.props;

    const selectedPatientDoctor = get(doctors, ['data', get(selectedPatient, 'doctorId')]);

    const renderData = {
      name: get(selectedPatient, 'name', null),
      doctor: get(selectedPatientDoctor, 'nameWithTitle', null),
      mobilePhone: get(selectedPatient, 'mobilePhone', null),
      homePhone: get(selectedPatient, 'homePhone', null),
      otherPhone: get(selectedPatient, 'otherPhone', null),
      email: get(selectedPatient, 'email', null),
      recommendedBy: get(selectedPatient, 'recommendedBy', null),
      insuranceCompany: get(selectedPatient, 'insuranceCompany', null),
    };

    const missing = missingPatientFields(selectedPatient, get(clinic, 'international'));
    const showPatientLink = !!get(clinic, 'showPatientProfileExternalLink');

    return (
      <Modal id="patientModal" className="secondaryModal patientInfoModal">
        <div className="modalHeader actionHeader">
          <Button className="backButton" onClick={this.handleClose}>
            <Icon iconName="arrow-left" className="icon" />
          </Button>
          <span className="backButtonLabel">{_('label/patient_info/title')}</span>
        </div>

        {isNew &&
          <ClickableList
            items={resetSelectionItems(_)}
            customRenderer={resetSelectionRenderer}
            onListItemClick={this.onChangePatient}
          />
        }

        <div className="selectedPatientInfoWrapper">
          <section className="patientInfoCard patientSummaryCard">
            <div className="patientSummaryHead">
              {isPresent(renderData.name) && <h3 className="patientSummaryName">{renderData.name}</h3>}
              {isPresent(renderData.doctor) && (
                <p className="patientSummaryDoctor">
                  {_('label/patient_info/responsible')}: {renderData.doctor}
                </p>
              )}
            </div>

            <ul className="patientSummaryRows">
              {isPresent(renderData.mobilePhone) && (
                <InfoRow label={_('label/patient_form/mobile_phone')} actions={<MobilePhoneLinks mobilePhone={renderData.mobilePhone} whatsapp />}>
                  <FormattedText type="phone" text={renderData.mobilePhone} />
                </InfoRow>
              )}

              {isPresent(renderData.homePhone) && (
                <InfoRow label={_('label/patient_form/homePhone')} actions={<MobilePhoneLinks mobilePhone={renderData.homePhone} />}>
                  <FormattedText type="phone" text={renderData.homePhone} />
                </InfoRow>
              )}

              {isPresent(renderData.otherPhone) && (
                <InfoRow label={_('label/patient_info/other_phone')} actions={<MobilePhoneLinks mobilePhone={renderData.otherPhone} whatsapp />}>
                  <FormattedText type="phone" text={renderData.otherPhone} />
                </InfoRow>
              )}

              {isPresent(renderData.email) && (
                <InfoRow label={_('label/patient_form/email')}>
                  <a href={`mailto:${String(renderData.email)}`}>{renderData.email}</a>
                </InfoRow>
              )}

              {isPresent(renderData.recommendedBy) && (
                <InfoRow label={_('label/patient_form/recommended_by')}>{renderData.recommendedBy}</InfoRow>
              )}

              {isPresent(renderData.insuranceCompany) && (
                <InfoRow label={_('label/patient_form/insurance_plan')}>{renderData.insuranceCompany}</InfoRow>
              )}
            </ul>

            {missing.length > 0 && (
              <div className="patientMissing">
                <p className="patientMissingTitle">
                  <Icon iconName="info" size={18} />
                  <span>{_('label/patient_info/missing')}</span>
                </p>
                <ul className="patientMissingFields">
                  {missing.map(field => <li key={field}>{_(MISSING_FIELD_LABELS[field])}</li>)}
                </ul>
                {!isNew && onOpenProfile && (
                  <button type="button" className="patientInfoTextButton" onClick={onOpenProfile}>
                    {_('action/patient_info/open_profile')}
                  </button>
                )}
              </div>
            )}
          </section>

          {showPatientLink && (
            <section className="patientInfoCard patientLinkCard">
              <div className="patientLinkHead">
                <span className="patientLinkIcon"><Icon iconName="mobile" size={20} /></span>
                <h4>{_('label/patient_info/link_title')}</h4>
              </div>
              <p className="patientLinkText">{_('label/patient_info/link_text')}</p>
              <ol className="patientLinkSteps">
                <li>{_('label/patient_info/link_step_open')}</li>
                <li>{_('label/patient_info/link_step_send')}</li>
                <li>{_('label/patient_info/link_step_expires')}</li>
              </ol>
              {isPresent(appointmentId) ? (
                <button type="button" className="patientLinkOpen" onClick={this.openPatientLinkPage} ref={this.bindPatientLinkOpen}>
                  {_('action/patient_info/open_link_page')}
                </button>
              ) : (
                <p className="patientLinkAfterSave">{_('label/patient_info/link_after_save')}</p>
              )}
            </section>
          )}

          {patientLoading && <Spinner />}
        </div>

      </Modal>
    );
  }

  // A click or a tap (detail > 0) lets go of the focus: desktop Chrome keeps it on a clicked button, and the Enter
  // that saves the appointment would only open the page again (bindPatientLinkOpen). Enter or Space on it (detail 0)
  // keep it there, so the keyboard doesn't lose its place.
  openPatientLinkPage = (event: Object) => {
    if (event.detail > 0) event.currentTarget.blur();
    const { accessToken, clinic, appointmentId } = this.props;
    if (!openInBrowser(patientLinkPageUrl(accessToken, get(clinic, 'id'), appointmentId))) {
      UserNotifications.i18nTextAlert('error/browser_unavailable');
    }
  };

  // Enter on the focused button opens the page (the browser clicks it) and is not Salvar: AppointmentModalForm
  // takes Enter as Salvar in a keydown listener on document
  bindPatientLinkOpen = keepKeysRef(isEnterKey);

  onChangePatient = () => {
    const { setModal, formChange } = this.props;
    formChange('patientId', null);
    setModal('PatientSelectModal');
  };

  handleClose = () => {
    this.props.setModal(null);
  };
}

const mapStateToProps = (state) => ({
  accessToken: state.auth.token,
});

export default connect(mapStateToProps)(PatientInfoModal);
